(function (global) {
  'use strict';
  if (global.MilroysTastingsStorefront) return;

  const VENUE_NAME = "Milroy's Tasting Room";
  const VENUE_ADDRESS = '3 Greek Street, Soho, London W1D 4NX';
  const DEFAULT_ORIGIN = 'https://3greekstreet.com';
  const DEFAULT_DOMAIN = '0e888b-50.myshopify.com';
  const DEFAULT_COLLECTION = 'tastings-calendar';
  const DEFAULT_API_VERSION = '2025-07';
  const UNKNOWN_SEATS = 8;
  const CACHE_PREFIX = 'milroys-tastings-storefront:';

  const MONTHS = {
    jan: 1,
    january: 1,
    feb: 2,
    february: 2,
    mar: 3,
    march: 3,
    apr: 4,
    april: 4,
    may: 5,
    jun: 6,
    june: 6,
    jul: 7,
    july: 7,
    aug: 8,
    august: 8,
    sep: 9,
    sept: 9,
    september: 9,
    oct: 10,
    october: 10,
    nov: 11,
    november: 11,
    dec: 12,
    december: 12,
  };

  const TRAILING_DATE =
    /\s+[–-]\s+(\d{1,2})(?:st|nd|rd|th)?\s+(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+(\d{4})\s*$/i;

  const SLASH_DATE = /\b(\d{1,2})\/(\d{1,2})\/(\d{2,4})\b/;

  const LONG_DATE =
    /\b(\d{1,2})(?:st|nd|rd|th)?\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})\b/i;

  const DAY_MONTH =
    /\b(\d{1,2})(?:st|nd|rd|th)?\s+(January|February|March|April|May|June|July|August|September|October|November|December)\b/i;

  const TIME_RANGE =
    /\b(\d{1,2}(?:[:.]\d{2})?)\s*(am|pm)?\s*[–-]\s*(\d{1,2}(?:[:.]\d{2})?)\s*(am|pm|hrs?|hours)?\b/i;

  const SINGLE_TIME = /\b(\d{1,2}(?:[:.]\d{2})?)\s*(am|pm)\b/i;

  const PRICE_CHUNK = /(?:£|&pound;|&#163;)\s*\d+(?:\.\d{2})?(?:\s*pp)?|\b\d+\.\d{2}\s*pp\b/gi;

  // ---------------------------------------------------------------------------
  // Title parsing
  // ---------------------------------------------------------------------------

  function lastSundayUTC(year, monthIndex) {
    const date = new Date(Date.UTC(year, monthIndex + 1, 0));
    date.setUTCDate(date.getUTCDate() - date.getUTCDay());
    return date;
  }

  function londonOffsetHours(year, month, day) {
    const probe = Date.UTC(year, month - 1, day, 12, 0, 0);
    const start = lastSundayUTC(year, 2);
    start.setUTCHours(1, 0, 0, 0);
    const end = lastSundayUTC(year, 9);
    end.setUTCHours(1, 0, 0, 0);
    return probe >= start.getTime() && probe < end.getTime() ? 1 : 0;
  }

  function toLondonIso(year, month, day, hour = 18, minute = 30) {
    const offset = londonOffsetHours(year, month, day);
    const pad = (value) => String(value).padStart(2, '0');
    return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:00+${pad(offset)}:00`;
  }

  function monthNumber(name) {
    return MONTHS[String(name || '').toLowerCase()] || 0;
  }

  function expandYear(value) {
    const year = Number(value);
    if (year >= 100) return year;
    return year >= 70 ? 1900 + year : 2000 + year;
  }

  function parseClock(raw, meridiem) {
    const clean = String(raw || '').trim().toLowerCase().replace('.', ':').replace(/hrs?|hours/g, '');
    const match = clean.match(/^(\d{1,2})(?::(\d{2}))?$/);
    if (!match) return null;
    let hour = Number(match[1]);
    const minute = Number(match[2] || 0);
    const period = String(meridiem || '').toLowerCase();
    if (period.startsWith('am') || period.startsWith('pm')) {
      if (hour === 12) hour = 0;
      if (period.startsWith('pm')) hour += 12;
    }
    if (hour > 23 || minute > 59) return null;
    return { hour, minute };
  }

  function parseTimes(title) {
    const text = String(title || '');
    const range = text.match(TIME_RANGE);
    if (range) {
      let startPeriod = range[2];
      let endPeriod = range[4];
      if (!startPeriod && endPeriod && /am|pm/i.test(endPeriod)) startPeriod = endPeriod;
      if (startPeriod && !endPeriod) endPeriod = startPeriod;
      const start = parseClock(range[1], startPeriod);
      const end = parseClock(range[3], endPeriod);
      if (start) return { start, end };
    }
    const single = text.match(SINGLE_TIME);
    if (single) {
      const start = parseClock(single[1], single[2]);
      if (start) {
        const endMinute = start.minute;
        const endHour = Math.min(23, start.hour + 1);
        return { start, end: { hour: endHour, minute: endMinute } };
      }
    }
    return {
      start: { hour: 18, minute: 30 },
      end: { hour: 20, minute: 0 },
    };
  }

  function parseEventDate(title) {
    const text = String(title || '').trim();
    if (!text) return null;

    const trailing = text.match(TRAILING_DATE);
    if (trailing) {
      const day = Number(trailing[1]);
      const month = monthNumber(trailing[2]);
      const year = Number(trailing[3]);
      if (month && day >= 1 && day <= 31) return { year, month, day, source: 'trailing' };
    }

    const longDate = text.match(LONG_DATE);
    if (longDate) {
      const day = Number(longDate[1]);
      const month = monthNumber(longDate[2]);
      const year = Number(longDate[3]);
      if (month && day >= 1 && day <= 31) return { year, month, day, source: 'long' };
    }

    const slash = text.match(SLASH_DATE);
    if (slash) {
      const day = Number(slash[1]);
      const month = Number(slash[2]);
      const year = expandYear(slash[3]);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return { year, month, day, source: 'slash' };
      }
    }

    return null;
  }

  function displayTitle(title) {
    return String(title || '')
      .replace(TRAILING_DATE, '')
      .replace(SLASH_DATE, '')
      .replace(LONG_DATE, '')
      .replace(DAY_MONTH, '')
      .replace(TIME_RANGE, '')
      .replace(SINGLE_TIME, '')
      .replace(PRICE_CHUNK, '')
      .replace(/[–-]\s*$/g, '')
      .replace(/\(\s*\)/g, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+[–-]\s+/g, ' ')
      .trim();
  }

  const SERIES_RULES = [
    [/peat/i, 'Peat Trails'],
    [/trip across scotland|classics of scotland/i, 'Scotland'],
    [/japan/i, 'Japan'],
    [/saturday/i, 'Saturday tastings'],
    [/december|winter/i, 'December nights'],
    [/blind/i, 'Blind tasting'],
    [/vintage|rare|take me back|archive/i, 'Vintage & rare'],
    [/anniversary|milroy/i, "Milroy's"],
    [/world/i, 'World whisky'],
  ];

  function seriesName(title) {
    const name = displayTitle(title);
    const rule = SERIES_RULES.find(([pattern]) => pattern.test(name));
    return rule ? rule[1] : name || 'Guided tasting';
  }

  function htmlToText(value) {
    return String(value || '')
      .replace(/\r\n/g, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&pound;/g, '£')
      .replace(/&#163;/g, '£')
      .replace(/&rsquo;|&#8217;|&#39;/g, "'")
      .replace(/&ldquo;|&rdquo;|&quot;/g, '"')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  function isGiftCard(product) {
    const type = String(product?.productType || product?.product_type || '').toLowerCase();
    const handle = String(product?.handle || '').toLowerCase();
    const title = String(product?.title || '').toLowerCase();
    return type.includes('gift') || handle.includes('gift-card') || title === 'gift card';
  }

  function parseTastingProduct(title) {
    const date = parseEventDate(title);
    if (!date) return null;
    const times = parseTimes(title);
    const startsAt = toLondonIso(date.year, date.month, date.day, times.start.hour, times.start.minute);
    const endsAt = times.end
      ? toLondonIso(date.year, date.month, date.day, times.end.hour, times.end.minute)
      : null;
    const name = displayTitle(title);
    return {
      date,
      startsAt,
      endsAt,
      title: name,
      series: seriesName(title),
    };
  }

  // ---------------------------------------------------------------------------
  // Product -> calendar event
  // ---------------------------------------------------------------------------

  function numericId(gidOrId) {
    if (gidOrId == null) return '';
    const value = String(gidOrId);
    const match = value.match(/(\d+)$/);
    return match ? match[1] : value;
  }

  function firstVariant(product) {
    return product.variants?.nodes?.[0] || product.variants?.[0] || null;
  }

  function firstImage(product) {
    return (
      product.featuredImage?.url ||
      product.featured_image ||
      product.images?.nodes?.[0]?.url ||
      product.images?.[0]?.src ||
      null
    );
  }

  function metafieldValue(field) {
    const value = field && typeof field === 'object' ? field.value : field;
    const clean = String(value ?? '').trim();
    return clean || null;
  }

  function validIso(value) {
    if (!value) return null;
    return Number.isNaN(new Date(value).getTime()) ? null : value;
  }

  function trimOrigin(origin) {
    return String(origin || DEFAULT_ORIGIN).replace(/\/$/, '');
  }

  function londonDatePath(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  function londonTimePath(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const hour = parts.find((part) => part.type === 'hour')?.value.padStart(2, '0');
    const minute = parts.find((part) => part.type === 'minute')?.value.padStart(2, '0');
    return `${hour}${minute}`;
  }

  function londonDayKey(isoOrDate) {
    const date = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
    if (Number.isNaN(date.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    return year && month && day ? `${year}-${month}-${day}` : '';
  }

  function eventPageUrl(origin, key) {
    return `${trimOrigin(origin)}/pages/tastings?view=event&date=${encodeURIComponent(key)}`;
  }

  function assignDatePaths(events, { shopOrigin = DEFAULT_ORIGIN } = {}) {
    const used = new Set();
    const origin = trimOrigin(shopOrigin);
    return (events || []).map((event) => {
      let key = londonDatePath(event.starts_at);
      if (!key) key = event.slug;
      if (used.has(key)) key = `${key}-${londonTimePath(event.starts_at) || event.slug}`;
      used.add(key);
      return {
        ...event,
        date_path: key,
        event_url: eventPageUrl(origin, key),
        checkout_url: `${origin}/pages/tastings?view=checkout&date=${encodeURIComponent(key)}`,
      };
    });
  }

  function tastingTimes(product) {
    const parsed = parseTastingProduct(product.title);
    const metaStart = validIso(metafieldValue(product.startsAt));
    const metaEnd = validIso(metafieldValue(product.endsAt));
    if (!metaStart) {
      return parsed ? { startsAt: parsed.startsAt, endsAt: metaEnd || parsed.endsAt, parsed } : null;
    }
    let endsAt = metaEnd;
    if (!endsAt && parsed?.endsAt && londonDayKey(parsed.endsAt) === londonDayKey(metaStart)) {
      endsAt = new Date(parsed.endsAt) > new Date(metaStart) ? parsed.endsAt : null;
    }
    return { startsAt: metaStart, endsAt, parsed };
  }

  function productToEvent(product, { shopOrigin = DEFAULT_ORIGIN } = {}) {
    if (!product || isGiftCard(product)) return null;
    const times = tastingTimes(product);
    if (!times) return null;

    const title = times.parsed?.title || displayTitle(product.title) || String(product.title || '').trim();
    const series = metafieldValue(product.series) || times.parsed?.series || seriesName(product.title);
    const variant = firstVariant(product);
    const variantId = numericId(variant?.legacyResourceId || variant?.id);
    const availableForSale = variant?.availableForSale ?? variant?.available ?? true;
    const rawQuantity = variant?.quantityAvailable ?? variant?.inventoryQuantity ?? variant?.inventory_quantity;
    const quantity = rawQuantity == null ? null : Number(rawQuantity);
    const seatsKnown = !availableForSale || (quantity != null && quantity > 0);
    let available = 0;
    if (availableForSale) available = seatsKnown ? quantity : UNKNOWN_SEATS;
    const price = Number(
      variant?.price?.amount ??
        variant?.price ??
        product.priceRange?.minVariantPrice?.amount ??
        product.priceRangeV2?.minVariantPrice?.amount ??
        0,
    );
    const origin = trimOrigin(shopOrigin);
    const description = htmlToText(product.descriptionHtml || product.body_html || product.description || '');
    const summary = description.replace(/\s+/g, ' ').split('. ').slice(0, 2).join('. ').slice(0, 280);
    const handle = product.handle;
    const capacity = Math.max(available, 1);

    return {
      id: numericId(product.id) || handle,
      slug: handle,
      date_path: londonDatePath(times.startsAt),
      title,
      name: title,
      summary: summary || series,
      description,
      image_url: firstImage(product),
      venue_name: VENUE_NAME,
      venue_address: VENUE_ADDRESS,
      starts_at: times.startsAt,
      ends_at: times.endsAt || null,
      visibility: 'public',
      status: String(product.status || 'ACTIVE').toLowerCase() === 'active' ? 'published' : 'draft',
      booking_mode: 'instant_purchase',
      capacity,
      allocated: 0,
      seats_known: seatsKnown,
      from_price: price,
      kicker: series,
      tags: series ? [series] : [],
      product_url: product.onlineStoreUrl || `${origin}/products/${handle}`,
      checkout_url: '',
      variant_id: variantId,
      ticket_types: [
        {
          id: variantId || handle,
          name: 'Ticket',
          description: 'Includes the hosted tasting.',
          price,
          capacity,
          quantity: capacity,
          available,
          min_per_booking: 1,
          max_per_booking: Math.min(8, capacity),
          sold_out: available <= 0,
        },
      ],
      showcase_content: {
        kicker: series,
        tags: [series],
        duration: times.endsAt ? 'About 90 minutes' : '',
        includes: 'Hosted tasting in the 1744 room',
        age: '18+ · ID may be requested',
      },
      source: 'shopify',
    };
  }

  function isCurrentEvent(event, now = new Date()) {
    const start = new Date(event?.starts_at || '');
    if (Number.isNaN(start.getTime())) return false;
    const end = event?.ends_at ? new Date(event.ends_at) : null;
    if (end && !Number.isNaN(end.getTime()) && end.getTime() >= start.getTime()) {
      return now.getTime() <= end.getTime();
    }
    const startDay = londonDayKey(start);
    const today = londonDayKey(now);
    return Boolean(startDay && today && startDay >= today);
  }

  function upcomingEvents(events, now = new Date()) {
    return (events || [])
      .filter((event) => isCurrentEvent(event, now))
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  }

  function cataloguePayload(events, { shopOrigin = DEFAULT_ORIGIN } = {}) {
    return {
      store: {
        found: true,
        name: '3 Greek Street',
        slug: '3-greek-street',
        currency: 'GBP',
        public_base_url: shopOrigin,
        venue_name: VENUE_NAME,
        venue_address: VENUE_ADDRESS,
      },
      events: assignDatePaths(events, { shopOrigin }),
    };
  }

  // ---------------------------------------------------------------------------
  // Event Calendar App (RSVPs and diary notes)
  // ---------------------------------------------------------------------------

  const ECA_ENDPOINT = 'https://api.eventcalendarapp.com';
  const ECA_USER_ID = 15808;
  const ECA_WIDGET_UUID = 'd7d2bfb9-1e30-4abd-b78c-df6a6902f441';

  function londonIsoFromLocal(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(value || ''));
    if (!match) return null;
    return toLondonIso(Number(match[1]), Number(match[2]), Number(match[3]), Number(match[4]), Number(match[5]));
  }

  function sessionLabel(name) {
    const clean = String(name || '').replace(/\s+/g, ' ').trim();
    const time = clean.replace(/^general admission\s*/i, '').trim();
    return time || clean || 'Session';
  }

  function ecaSeatsLeft(type) {
    if (type?.quantity == null) return null;
    return Math.max(0, Number(type.quantity) - Number(type.totalActive || 0));
  }

  function noticeKind(event) {
    const title = String(event?.summary || '').replace(/\s+/g, ' ').trim();
    if (/^closed$/i.test(title)) return 'closed';
    if (/barrel\s*top/i.test(title)) return 'barrel';
    if (/birthday flight/i.test(title)) return 'flights';
    if (/brand\s+(tasting|event)/i.test(title)) return 'brand';
    return null;
  }

  function noticeCopy(kind, event) {
    const title = String(event.summary || '').replace(/\s+/g, ' ').trim();
    const description = htmlToText(event.longDescription || event.description || '');
    if (kind === 'closed') {
      return {
        title: 'Closed',
        summary: 'The tasting room is closed.',
        description: 'The tasting room is closed. There are no tastings on this day.',
        kicker: 'Closed',
        notice_label: 'Closed',
        notice_detail: 'No tastings',
        tags: ['Closed'],
        all_day: true,
        includes: 'The tasting room is closed',
      };
    }
    if (kind === 'barrel') {
      return {
        title: 'Barrel-top tasting',
        summary: 'A walk-in barrel-top tasting in the whisky bar, from 4:00pm until 8:00pm. No booking needed.',
        description,
        kicker: 'Open evening',
        notice_label: 'Walk in',
        notice_detail: 'No booking needed',
        tags: ['Open evening'],
        includes: 'Walk-in pours in the whisky bar',
      };
    }
    if (kind === 'flights') {
      return {
        title: "Milroy's birthday flight days",
        summary: 'A mystery flight of three Milroy’s whiskies, poured in the bar on Sunday 8 and Monday 9 November.',
        description,
        kicker: 'Anniversary',
        notice_label: 'In the bar',
        notice_detail: 'No booking needed',
        tags: ['Anniversary'],
        includes: 'Three whiskies, poured in the bar',
      };
    }
    const named = /morrison/i.test(title);
    return {
      title: named ? 'Brand tasting with Morrison' : /event/i.test(title) ? 'Brand event' : 'Brand tasting',
      summary: 'Details for this brand tasting are still to be announced.',
      description:
        description && !/^brand (tasting|event) tba$/i.test(description)
          ? description
          : 'Details for this brand tasting are still to be announced.',
      kicker: 'Brand tasting',
      notice_label: 'To be announced',
      notice_detail: 'Details to follow',
      tags: ['Brand tasting'],
      includes: 'Details to follow',
    };
  }

  function ecaNoticeToCatalogue(event) {
    const kind = noticeKind(event);
    if (!kind) return null;
    if (event?.rsvp || event?.shopifyProductId) return null;
    const startsAt = londonIsoFromLocal(event.timezoneStart);
    if (!startsAt) return null;
    const copy = noticeCopy(kind, event);
    const endsAt = copy.all_day ? null : londonIsoFromLocal(event.timezoneEnd);
    return {
      id: `eca-${event.uuid}`,
      slug: `eca-${event.uuid}`,
      date_path: '',
      title: copy.title,
      name: copy.title,
      summary: copy.summary,
      description: copy.description,
      image_url: event.image || '',
      venue_name: event.venueName || "Milroy's, 3 Greek Street",
      venue_address: event.location?.description || VENUE_ADDRESS,
      starts_at: startsAt,
      ends_at: endsAt,
      all_day: Boolean(copy.all_day),
      visibility: 'public',
      status: 'published',
      booking_mode: 'notice',
      notice_label: copy.notice_label,
      notice_detail: copy.notice_detail,
      capacity: 0,
      allocated: 0,
      from_price: null,
      kicker: copy.kicker,
      tags: copy.tags,
      product_url: '',
      checkout_url: '',
      variant_id: null,
      ticket_types: [],
      showcase_content: {
        kicker: copy.kicker,
        tags: copy.tags,
        includes: copy.includes,
        age: '18+ · ID may be requested',
      },
      source: 'eventcalendar',
    };
  }

  function ecaEventToCatalogue(event, { userId, widgetUuid, timezone = 'Europe/London' } = {}) {
    if (!event?.rsvp || event.shopifyProductId || event.salesEnded) return null;
    if (event.salesStarted && event.salesStarted.value === false) return null;
    const startsAt = londonIsoFromLocal(event.timezoneStart);
    if (!startsAt) return null;
    const ticketTypes = (event.ticketTypes || []).filter((type) => !type.deleted);
    if (!ticketTypes.length) return null;

    const free = !event.rsvpPayment;
    const description = htmlToText(event.longDescription || event.description || '');
    const sessions = ticketTypes.map((type) => {
      const left = ecaSeatsLeft(type);
      const open = left == null ? UNKNOWN_SEATS : left;
      return {
        id: String(type.id),
        name: sessionLabel(type.name),
        description: free ? 'Free place. Booking is required.' : 'Includes the hosted tasting.',
        price: free ? 0 : Number(type.cost || 0),
        capacity: type.quantity == null ? open : Number(type.quantity),
        quantity: type.quantity == null ? open : Number(type.quantity),
        available: open,
        min_per_booking: 1,
        max_per_booking: event.disableMultipleTickets ? 1 : Math.min(8, Math.max(open, 1)),
        sold_out: open <= 0,
      };
    });
    const title = String(event.summary || event.eventShortDescription || 'Tasting').replace(/\s+/g, ' ').trim();
    const summary = [htmlToText(event.shortDescription || ''), `Sessions: ${sessions.map((type) => type.name).join(', ')}.`]
      .filter(Boolean)
      .join(' ');

    return {
      id: `eca-${event.uuid}`,
      slug: event.friendlyUrl || event.uuid,
      date_path: '',
      title,
      name: title,
      summary: summary.slice(0, 320),
      description,
      image_url: event.image || '',
      venue_name: VENUE_NAME,
      venue_address: event.location?.description || VENUE_ADDRESS,
      starts_at: startsAt,
      ends_at: ticketTypes.length > 1 ? null : londonIsoFromLocal(event.timezoneEnd),
      visibility: 'public',
      status: 'published',
      booking_mode: 'eca_rsvp',
      capacity: sessions.reduce((sum, type) => sum + Number(type.available || 0), 0),
      allocated: 0,
      from_price: free ? 0 : Math.min(...sessions.map((type) => type.price)),
      kicker: free ? 'Free tasting' : 'Guided tasting',
      tags: free ? ['Free tasting'] : [],
      product_url: '',
      checkout_url: '',
      variant_id: null,
      ticket_types: sessions,
      showcase_content: {
        kicker: free ? 'Free tasting' : 'Guided tasting',
        tags: [free ? 'Free tasting' : 'Guided tasting'],
        duration: ticketTypes.length > 1 ? `${ticketTypes.length} sessions` : '30 minutes',
        includes: 'Hosted tasting. A place must be reserved.',
        age: '18+ · ID may be requested',
      },
      eca: {
        userId,
        widgetUuid,
        calendarId: event.calendar_id,
        eventUuid: event.uuid,
        eventTitle: event.summary,
        timezoneStart: event.timezoneStart,
        timezoneEnd: event.timezoneEnd,
        utcStartTime: event.utcStartTime,
        utcEndTime: event.utcEndTime,
        timezone,
        timezoneAbbreviation: event.timezoneAbbreviation,
        currency: event.currency || 'GBP',
        repeatId: event.repeatId || event.ecaRepeatId || null,
        location: event.location || null,
        longDescription: event.longDescription || event.description || '',
        confirmationMessage: htmlToText(event.rsvpConfirmationMessage || ''),
      },
      source: 'eventcalendar',
    };
  }

  function rsvpRequestBody({ event, name, email, quantities }) {
    const meta = event?.eca || {};
    const ticketTypes = {};
    for (const [id, qty] of Object.entries(quantities || {})) {
      const quantity = Number(qty || 0);
      if (quantity > 0) ticketTypes[String(id)] = quantity;
    }
    return {
      name: String(name || '').trim(),
      email: String(email || '').trim(),
      ticketTypes,
      eventTitle: meta.eventTitle,
      eventUuid: meta.eventUuid,
      userId: meta.userId,
      widgetuuid: meta.widgetUuid,
      calendarId: meta.calendarId,
      timezoneStart: meta.timezoneStart,
      timezoneEnd: meta.timezoneEnd,
      currency: meta.currency,
      repeatId: meta.repeatId ?? null,
      longDescription: meta.longDescription || '',
      location: meta.location || null,
      utcEndTime: meta.utcEndTime,
      utcStartTime: meta.utcStartTime,
      timezone: meta.timezone,
      couponCode: null,
      timezoneAbbreviation: meta.timezoneAbbreviation,
    };
  }

  function defaultFetch(...args) {
    return global.fetch(...args);
  }

  async function fetchUpcomingRsvpEvents({
    userId = ECA_USER_ID,
    widgetUuid = ECA_WIDGET_UUID,
    now = new Date(),
    fetchImpl = defaultFetch,
  } = {}) {
    const settingsResponse = await fetchImpl(`${ECA_ENDPOINT}/settings?id=${userId}&widgetUuid=${widgetUuid}`);
    if (!settingsResponse.ok) throw new Error(`Event Calendar settings failed (${settingsResponse.status})`);
    const settings = await settingsResponse.json();
    const timezone = settings.timezone || 'Europe/London';
    const today = londonDayKey(now);

    async function loadPage(page) {
      const response = await fetchImpl(`${ECA_ENDPOINT}/events?id=${userId}&widgetUuid=${widgetUuid}&page=${page}`);
      if (!response.ok) throw new Error(`Event Calendar events failed (${response.status})`);
      return response.json();
    }

    function collect(data) {
      const rows = data.events || [];
      const mapped = [];
      for (const event of rows) {
        const row = ecaEventToCatalogue(event, { userId, widgetUuid, timezone }) || ecaNoticeToCatalogue(event);
        if (row && isCurrentEvent(row, now)) mapped.push(row);
      }
      const allPast = rows.every((event) => String(event.timezoneEnd || event.timezoneStart || '').slice(0, 10) < today);
      return { mapped, allPast };
    }

    // Pages run oldest first, so walk back from the last page until two pages in a row are entirely past.
    const first = await loadPage(1);
    const total = Math.min(Number(first.pages?.total || 1), 80);
    const events = [];
    let pastRun = 0;
    for (let page = total; page > 1 && pastRun < 2; page -= 4) {
      const batch = [];
      for (let offset = 0; offset < 4 && page - offset > 1; offset += 1) batch.push(page - offset);
      const results = await Promise.all(batch.map(loadPage));
      for (const data of results) {
        const { mapped, allPast } = collect(data);
        events.push(...mapped);
        pastRun = allPast ? pastRun + 1 : 0;
        if (pastRun >= 2) break;
      }
    }
    if (pastRun < 2) events.push(...collect(first).mapped);
    return events;
  }

  // ---------------------------------------------------------------------------
  // Storefront API
  // ---------------------------------------------------------------------------

  function storefrontQuery({ withToken }) {
    const metafields = withToken
      ? `
          startsAt: metafield(namespace: "tastings", key: "starts_at") { value }
          endsAt: metafield(namespace: "tastings", key: "ends_at") { value }
          series: metafield(namespace: "tastings", key: "series") { value }`
      : '';
    const inventory = withToken ? 'quantityAvailable' : '';
    return `
      query Tastings($handle: String!, $cursor: String) {
        collection(handle: $handle) {
          products(first: 100, after: $cursor) {
            pageInfo { hasNextPage endCursor }
            nodes {
              id handle title descriptionHtml onlineStoreUrl productType
              featuredImage { url }${metafields}
              variants(first: 1) { nodes { id availableForSale ${inventory} price { amount } } }
            }
          }
        }
      }
    `;
  }

  async function fetchStorefrontProducts({
    domain = DEFAULT_DOMAIN,
    token = '',
    collection = DEFAULT_COLLECTION,
    apiVersion = DEFAULT_API_VERSION,
    fetchImpl = defaultFetch,
  } = {}) {
    const host = String(domain || DEFAULT_DOMAIN).replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    const endpoint = `https://${host}/api/${apiVersion || DEFAULT_API_VERSION}/graphql.json`;
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const cleanToken = String(token || '').trim();
    if (cleanToken) headers['X-Shopify-Storefront-Access-Token'] = cleanToken;
    const query = storefrontQuery({ withToken: Boolean(cleanToken) });
    const nodes = [];
    let cursor = null;
    for (let page = 0; page < 10; page += 1) {
      const response = await fetchImpl(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, variables: { handle: collection || DEFAULT_COLLECTION, cursor } }),
      });
      if (!response.ok) throw new Error(`3 Greek Street tastings could not be loaded (${response.status}).`);
      const payload = await response.json();
      if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join('; '));
      const products = payload.data?.collection?.products;
      if (!products) break;
      nodes.push(...products.nodes);
      if (!products.pageInfo?.hasNextPage) break;
      cursor = products.pageInfo.endCursor;
    }
    return nodes;
  }

  function sessionStore() {
    try {
      return global.sessionStorage || null;
    } catch {
      return null;
    }
  }

  function readCache(storage, key, maxAge, now) {
    if (!storage || !maxAge) return null;
    try {
      const cached = JSON.parse(storage.getItem(key) || 'null');
      if (cached && now.getTime() - Number(cached.at) < maxAge) return cached.catalogue;
    } catch {
      /* ignore bad cache */
    }
    return null;
  }

  function writeCache(storage, key, catalogue, now) {
    if (!storage) return;
    try {
      storage.setItem(key, JSON.stringify({ at: now.getTime(), catalogue }));
    } catch {
      /* storage full or blocked */
    }
  }

  async function fetchStorefrontCatalogue({
    domain = DEFAULT_DOMAIN,
    token = '',
    collection = DEFAULT_COLLECTION,
    apiVersion = DEFAULT_API_VERSION,
    shopOrigin = DEFAULT_ORIGIN,
    includeRsvp = true,
    cacheMs = 5 * 60 * 1000,
    storage = sessionStore(),
    fetchImpl = defaultFetch,
    now = new Date(),
  } = {}) {
    const cacheKey = `${CACHE_PREFIX}${domain}:${collection}:${token ? 'token' : 'public'}`;
    const cached = readCache(storage, cacheKey, cacheMs, now);
    if (cached) return cached;

    const [products, rsvps] = await Promise.all([
      fetchStorefrontProducts({ domain, token, collection, apiVersion, fetchImpl }),
      includeRsvp
        ? fetchUpcomingRsvpEvents({ now, fetchImpl }).catch((error) => {
            console.warn(`Event Calendar tastings unavailable: ${error.message}`);
            return [];
          })
        : [],
    ]);
    const events = products.map((product) => productToEvent(product, { shopOrigin })).filter(Boolean);
    const catalogue = cataloguePayload(upcomingEvents([...events, ...rsvps], now), { shopOrigin });
    writeCache(storage, cacheKey, catalogue, now);
    return catalogue;
  }

  function slugify(text) {
    return String(text || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/['’]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
      .replace(/-+$/, '');
  }

  // One stable URL slug per tasting: clean title plus date. Same-day twins get the time added.
  // Ordered by product id so a new tasting never changes an existing tasting's address.
  function assignEventPageSlugs(events) {
    const used = new Set();
    const slugs = new Map();
    const ordered = (events || [])
      .filter(Boolean)
      .sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0) || String(a.id).localeCompare(String(b.id)));
    for (const event of ordered) {
      const base = [slugify(event.title), londonDatePath(event.starts_at)].filter(Boolean).join('-') || String(event.id);
      let slug = base;
      if (used.has(slug)) slug = `${base}-${londonTimePath(event.starts_at) || event.id}`;
      for (let n = 2; used.has(slug); n += 1) slug = `${base}-${n}`;
      used.add(slug);
      slugs.set(String(event.id), slug);
    }
    return slugs;
  }

  function whenText(startsAt, endsAt) {
    const start = new Date(startsAt);
    if (Number.isNaN(start.getTime())) return '';
    const day = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'long', day: 'numeric', month: 'long' }).format(start);
    const clock = (date) =>
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: 'numeric', minute: '2-digit', hour12: true })
        .format(date)
        .replace(/\s/g, ' ')
        .toLowerCase();
    const end = endsAt ? new Date(endsAt) : null;
    const range = end && !Number.isNaN(end.getTime()) && end > start ? `${clock(start)} – ${clock(end)}` : clock(start);
    return `${day} · ${range}`;
  }

  function priceText(price) {
    const amount = Number(price);
    if (!Number.isFinite(amount) || amount <= 0) return '';
    return `£${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
  }

  // The values stored on the milroysofsoho.com "Tasting event" entry for one tasting.
  function eventPageFields(event, slug) {
    const when = whenText(event.starts_at, event.ends_at);
    const ticket = (event.ticket_types || [])[0] || {};
    const soldOut = Boolean(ticket.sold_out);
    const seats = event.seats_known && !soldOut && Number.isFinite(Number(ticket.available)) ? Number(ticket.available) : null;
    const price = priceText(event.from_price);
    const seoTitle = `${event.title} | Whisky Tasting London`;
    const seoDescription = `${event.title}: ${when}, upstairs at 3 Greek Street, Soho.${price ? ` Tickets from ${price}.` : ''}`.slice(0, 155);
    return {
      handle: slug,
      fields: {
        title: event.title,
        series: event.kicker || '',
        when_text: when,
        starts_at: event.starts_at,
        ends_at: event.ends_at || '',
        description: event.description || '',
        image_url: event.image_url || '',
        price: Number(event.from_price || 0).toFixed(2),
        price_text: price,
        currency: 'GBP',
        variant_id: String(event.variant_id || ''),
        available: soldOut ? 'false' : 'true',
        seats_left: seats == null ? '' : String(seats),
        event_key: event.date_path || '',
        seo_title: seoTitle,
        seo_description: seoDescription,
      },
    };
  }

  // The live details of one tasting (seats, sold out, price, start time), read from 3 Greek Street.
  async function fetchLiveEvent({
    domain = DEFAULT_DOMAIN,
    token = '',
    apiVersion = DEFAULT_API_VERSION,
    variantId,
    fetchImpl = defaultFetch,
  } = {}) {
    const id = numericId(variantId);
    if (!id) return null;
    const host = String(domain || DEFAULT_DOMAIN).replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    const cleanToken = String(token || '').trim();
    const extra = cleanToken
      ? 'quantityAvailable product { startsAt: metafield(namespace: "tastings", key: "starts_at") { value } endsAt: metafield(namespace: "tastings", key: "ends_at") { value } }'
      : '';
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (cleanToken) headers['X-Shopify-Storefront-Access-Token'] = cleanToken;
    const response = await fetchImpl(`https://${host}/api/${apiVersion || DEFAULT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: `query Live($id: ID!) { node(id: $id) { ... on ProductVariant { availableForSale price { amount } ${extra} } } }`,
        variables: { id: `gid://shopify/ProductVariant/${id}` },
      }),
    });
    if (!response.ok) return null;
    const payload = await response.json();
    const variant = payload.data && payload.data.node;
    if (!variant || payload.errors) return null;
    const quantity = variant.quantityAvailable == null ? null : Number(variant.quantityAvailable);
    const price = variant.price && variant.price.amount != null ? Number(variant.price.amount) : null;
    return {
      found: true,
      availableForSale: Boolean(variant.availableForSale),
      seats: Number.isFinite(quantity) ? quantity : null,
      price: Number.isFinite(price) ? price : null,
      startsAt: (variant.product && variant.product.startsAt && variant.product.startsAt.value) || null,
      endsAt: (variant.product && variant.product.endsAt && variant.product.endsAt.value) || null,
    };
  }

  const CART_CREATE_MUTATION = `mutation CartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart { id checkoutUrl totalQuantity }
      userErrors { field message code }
    }
  }`;

  // Creates a cart on the 3 Greek Street store from any site and returns its checkout link.
  async function createCheckoutCart({
    domain = DEFAULT_DOMAIN,
    token = '',
    apiVersion = DEFAULT_API_VERSION,
    variantId,
    quantity,
    attributes = [],
    fetchImpl = defaultFetch,
  } = {}) {
    const host = String(domain || DEFAULT_DOMAIN).replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    const id = numericId(variantId);
    const count = Math.floor(Number(quantity));
    if (!id) throw new Error('This tasting cannot be booked online yet.');
    if (!Number.isFinite(count) || count < 1) throw new Error('Choose at least one ticket.');
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const cleanToken = String(token || '').trim();
    if (cleanToken) headers['X-Shopify-Storefront-Access-Token'] = cleanToken;
    const lineAttributes = (attributes || [])
      .filter((row) => row && row.key && row.value != null && String(row.value) !== '')
      .map((row) => ({ key: String(row.key), value: String(row.value) }));
    const response = await fetchImpl(`https://${host}/api/${apiVersion || DEFAULT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: CART_CREATE_MUTATION,
        variables: {
          input: {
            lines: [{ merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity: count, attributes: lineAttributes }],
          },
        },
      }),
    });
    if (!response.ok) throw new Error(`Could not start your booking (${response.status}). Please try again.`);
    const payload = await response.json();
    if (payload.errors && payload.errors.length) throw new Error('Could not start your booking. Please try again.');
    const result = payload.data && payload.data.cartCreate;
    const problem = result && result.userErrors && result.userErrors[0];
    if (problem) throw new Error(problem.message || 'Could not add tickets to the cart.');
    const checkoutUrl = result && result.cart && result.cart.checkoutUrl;
    if (!checkoutUrl || !/^https:\/\//.test(checkoutUrl)) throw new Error('Could not start your booking. Please try again.');
    return { cartId: result.cart.id, checkoutUrl };
  }

  global.MilroysTastingsStorefront = {
    DEFAULT_API_VERSION,
    DEFAULT_COLLECTION,
    DEFAULT_DOMAIN,
    ECA_ENDPOINT,
    ECA_USER_ID,
    ECA_WIDGET_UUID,
    assignDatePaths,
    assignEventPageSlugs,
    cataloguePayload,
    createCheckoutCart,
    displayTitle,
    eventPageFields,
    fetchLiveEvent,
    priceText,
    whenText,
    ecaEventToCatalogue,
    ecaNoticeToCatalogue,
    fetchStorefrontCatalogue,
    fetchStorefrontProducts,
    fetchUpcomingRsvpEvents,
    htmlToText,
    isCurrentEvent,
    isGiftCard,
    londonDatePath,
    londonDayKey,
    londonOffsetHours,
    londonTimePath,
    noticeKind,
    parseEventDate,
    parseTastingProduct,
    parseTimes,
    productToEvent,
    rsvpRequestBody,
    seriesName,
    sessionLabel,
    storefrontQuery,
    toLondonIso,
    upcomingEvents,
  };
})(typeof window !== 'undefined' ? window : globalThis);
